import { supabase } from '../lib/supabase';
import type { DeclaracaoNaoAssociacao } from '../../types';

/**
 * Declaração de não associação — leitura direta da tabela e escrita só pela
 * edge function `declaracao-nao-associacao` (a tabela não aceita escrita da tela).
 * Design: docs/superpowers/specs/2026-10-16-declaracao-nao-associacao-design.md
 */

const COLUNAS = '*, emissor:profiles(full_name)';

/** Todas as declarações da negociação, a mais recente primeiro (é a que vale). */
export async function listarDeclaracoes(negociacaoId: number): Promise<DeclaracaoNaoAssociacao[]> {
    const { data, error } = await supabase
        .from('declaracoes_nao_associacao')
        .select(COLUNAS)
        .eq('negociacao_id', negociacaoId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as DeclaracaoNaoAssociacao[];
}

/** Ids, entre os informados, das negociações que já têm declaração (encerradas). */
export async function negociacoesComDeclaracao(ids: number[]): Promise<Set<number>> {
    if (ids.length === 0) return new Set();
    const { data, error } = await supabase
        .from('declaracoes_nao_associacao')
        .select('negociacao_id')
        .in('negociacao_id', ids);
    if (error) throw error;
    return new Set((data || []).map((d: { negociacao_id: number }) => Number(d.negociacao_id)));
}

export interface ResultadoDeclaracao {
    declaracao: DeclaracaoNaoAssociacao | null;
    /** Sucesso parcial (ex.: assinatura do procurador ainda não voltou). */
    aviso: string | null;
    erro: string | null;
}

/**
 * `functions.invoke` engole a causa em resposta não-2xx ("Edge Function returned
 * a non-2xx status code"); o motivo real fica em `error.context`. Mesmo padrão de
 * src/lib/gatewayResolver.ts.
 */
async function chamar(body: Record<string, unknown>, padrao: string): Promise<ResultadoDeclaracao> {
    const { data, error } = await supabase.functions.invoke('declaracao-nao-associacao', { body });
    if (error) {
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        return { declaracao: null, aviso: null, erro: detalhe?.error || error.message || padrao };
    }
    if (!data?.success) return { declaracao: null, aviso: null, erro: data?.error || padrao };
    return { declaracao: data.declaracao ?? null, aviso: data.aviso ?? null, erro: null };
}

export function emitirDeclaracao(negociacaoId: number): Promise<ResultadoDeclaracao> {
    return chamar({ acao: 'emitir', negociacao_id: negociacaoId }, 'Não foi possível emitir a declaração.');
}

/** Tenta a assinatura automática de novo e relê o Autentique ("Verificar de novo"). */
export function atualizarDeclaracao(declaracaoId: number): Promise<ResultadoDeclaracao> {
    return chamar({ acao: 'atualizar', declaracao_id: declaracaoId }, 'Não foi possível verificar a declaração.');
}
